import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, X } from "lucide-react";
import type { CustomerClass, CustomerFollowerDto } from "@hisense/shared";
import { Select } from "@/components/ui/Select";
import { Hint } from "@/components/ui/FormLayout";
import { useUserOptions } from "@/api/hooks";

/**
 * Người theo đơn của một khách (跟单员) — nhiều người (Roary 29/09/2026: "sometimes some guests have two order trackers working
 * together").
 *
 * Mỗi người một chip có nút bỏ, thêm người bằng một ô chọn — ô chọn gốc của hệ, dùng được bằng bàn phím và trên điện thoại. Ô
 * chọn chỉ đưa ra người thấy được khách của khu vực đang chọn (máy chủ lọc `forRegion`). Người đã có tên mà không còn trong danh
 * sách đó — khác khu vực, tài khoản đã khoá — vẫn hiện, kèm cảnh báo bằng chữ và biểu tượng, để người sửa tự quyết bỏ ra: máy
 * chủ không bắt bỏ họ mới cho lưu.
 */
export function FollowersPicker({ id, value, onChange, region, known, lockedId, disabled, warning, label, addLabel, hint }: {
  id: string;
  value: readonly string[];
  onChange: (next: string[]) => void;
  /** `null`: nhà cung cấp chưa phân đội (0097) — mọi nhân viên đều thấy được nó. */
  region: CustomerClass | null;
  /** Người theo đơn đang lưu trên khách — lấy tên cho người không còn trong ô chọn. */
  known: readonly CustomerFollowerDto[];
  /** Tài khoản "chỉ khách mình theo đơn": tên mình không bỏ được (máy chủ cũng giữ lại). */
  lockedId?: string | null;
  disabled?: boolean;
  /** Cảnh báo thêm của trang (khách riêng tư, 30/09/2026) — đứng cùng các cảnh báo khác, trước dòng gợi ý. */
  warning?: string | null;
  /** Nhãn danh sách, ô thêm người và dòng gợi ý cuối — người theo dõi nhà cung cấp (0097) nói khác người theo đơn của khách. */
  label?: string;
  addLabel?: string;
  hint?: string;
}) {
  const { t } = useTranslation();
  const options = useUserOptions(region ?? undefined);
  const offered = options.data ?? [];
  // Tên của người vừa thêm trong lần sửa này: đổi phân loại khách thì ô chọn tải danh sách của khu vực kia, không còn tên họ.
  const [added, setAdded] = useState<Record<string, string>>({});
  const nameOf = (uid: string) =>
    offered.find((u) => u.id === uid)?.displayName ?? added[uid] ?? known.find((f) => f.id === uid)?.name ?? uid;
  // Chỉ đánh dấu khi đã tải xong danh sách — đang tải thì chưa biết ai "không thấy được khách".
  const unreachable = options.data ? value.filter((uid) => !offered.some((u) => u.id === uid)) : [];
  const addable = offered.filter((u) => !value.includes(u.id));

  return (
    <div className="space-y-2">
      {value.length ? (
        <ul className="flex flex-wrap gap-1.5" aria-label={label ?? t("customers.cs")}>
          {value.map((uid) => {
            const warn = unreachable.includes(uid);
            const locked = uid === lockedId;
            return (
              <li
                key={uid}
                className={`inline-flex max-w-full items-center gap-1 rounded-sm border px-2 py-1 text-sm ${warn ? "border-warn bg-warn-tint text-warn" : "border-line bg-paper text-ink"}`}
              >
                {warn ? <AlertTriangle className="size-3.5 shrink-0" aria-hidden="true" /> : null}
                <span className="min-w-0 truncate">{nameOf(uid)}</span>
                {locked ? null : (
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => onChange(value.filter((v) => v !== uid))}
                    aria-label={t("customers.followerRemove", { name: nameOf(uid) })}
                    className="-mr-1 rounded-xs p-0.5 text-ink-soft hover:bg-sunken hover:text-ink disabled:opacity-50"
                  >
                    <X className="size-3.5" aria-hidden="true" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      ) : null}
      <Select
        id={id}
        containerClassName="w-full"
        disabled={disabled || options.isPending || !addable.length}
        value=""
        onChange={(e) => {
          const uid = e.target.value;
          if (!uid) return;
          const name = offered.find((u) => u.id === uid)?.displayName;
          if (name) setAdded((prev) => ({ ...prev, [uid]: name }));
          onChange([...value, uid]);
        }}
      >
        <option value="">{options.isPending ? t("common.loading") : addLabel ?? t("customers.followerAdd")}</option>
        {addable.map((u) => (
          <option key={u.id} value={u.id}>{u.displayName}</option>
        ))}
      </Select>
      {options.isError ? <Hint tone="warn">{t("customers.followerOptionsError")}</Hint> : null}
      {unreachable.length ? (
        <Hint tone="warn">{t("customers.followerUnreachable", { names: unreachable.map(nameOf).join(", ") })}</Hint>
      ) : null}
      {warning ? <Hint tone="warn">{warning}</Hint> : null}
      {lockedId && value.includes(lockedId) ? <Hint>{t("customers.followerSelfLocked")}</Hint> : null}
      <Hint>{hint ?? (value.length ? t("customers.followersHint") : t("customers.followersNone"))}</Hint>
    </div>
  );
}
